import React, { useMemo } from 'react';
import { Container } from '@mui/material';
import { useActiveRole } from '../../../hooks/useActiveRole';
import { UserRole } from '../../authentication/types';
import { QuickActionsWidget, HowItWorksWidget, ResourcesWidget, QuickStatsWidget } from '../components';

/**
 * Role-specific dashboard configuration
 */
interface DashboardConfig {
  showQuickStats: boolean;
  showQuickActions: boolean;
  showQRCodeAction: boolean;
  showReportsAction: boolean;
  showHowItWorks: boolean;
  showFAQ: boolean;
}

/**
 * Get dashboard configuration based on user role
 */
const getDashboardConfig = (role: UserRole | null): DashboardConfig => {
  switch (role) {
    case UserRole.SYSTEM_ADMIN:
      return {
        showQuickStats: true,
        showQuickActions: false,
        showQRCodeAction: false,
        showReportsAction: false,
        showHowItWorks: false,
        showFAQ: false,
      };

    case UserRole.UNIVERSITY_ADMIN:
      return {
        showQuickStats: true,
        showQuickActions: false,
        showQRCodeAction: false,
        showReportsAction: false,
        showHowItWorks: true,
        showFAQ: false,
      };

    case UserRole.FACULTY_ADMIN:
      return {
        showQuickStats: true,
        showQuickActions: false,
        showQRCodeAction: false,
        showReportsAction: false,
        showHowItWorks: true,
        showFAQ: false,
      };

    case UserRole.PROFESSOR:
      return {
        showQuickStats: false,
        showQuickActions: true,
        showQRCodeAction: true,
        showReportsAction: true,
        showHowItWorks: true,
        showFAQ: false,
      };

    case UserRole.COURSE_REPRESENTATIVE:
      return {
        showQuickStats: false,
        showQuickActions: true,
        showQRCodeAction: false, // Hide QR Code for student representatives
        showReportsAction: true, // Keep Reports
        showHowItWorks: true,
        showFAQ: false,
      };

    default:
      // Default fallback (safest option - show everything)
      return {
        showQuickStats: true,
        showQuickActions: true,
        showQRCodeAction: true,
        showReportsAction: true,
        showHowItWorks: true,
        showFAQ: false,
      };
  }
};

/**
 * Comprehensive dashboard landing page with role-specific customization
 * Provides overview of survey system features and quick actions based on user role
 */
const DashboardPage: React.FC = () => {
  const { currentRole } = useActiveRole();

  // Get role-specific dashboard configuration
  const config = useMemo(() => getDashboardConfig(currentRole), [currentRole]);

  return (
    <Container maxWidth={false} disableGutters>
      {/* Quick Stats - Role-specific analytics snapshot */}
      {config.showQuickStats && <QuickStatsWidget />}

      {/* Quick Actions - Role-specific */}
      {config.showQuickActions && (
        <QuickActionsWidget
          showQRCode={config.showQRCodeAction}
          showReports={config.showReportsAction}
        />
      )}

      {/* How It Works - Role-specific */}
      {config.showHowItWorks && <HowItWorksWidget />}

      {/* Resources - Always shown, but FAQ is role-specific */}
      <ResourcesWidget showFAQ={config.showFAQ} />
    </Container>
  );
};

export default DashboardPage;
